import type { AxiosResponse } from 'axios';
import axiosInstance from './axiosInstance';
const baseUrl = `${SUPP_ADMIN_BASE_URL}/supp-management`;

/** ArtworkAiLiblibStyleDictDTO 类型定义 */
export interface ArtworkAiLiblibStyleDictDTO {
  styleId?: number;
  styleType?: string;
  styleTypeJa?: string;
  styleTypeSort?: number;
  styleName?: string;
  styleNameJa?: string;
  styleNameSort?: number;
  styleImage?: string;
  triggerWord?: string;
  positivePrompt?: string;
  positivePromptJa?: string;
  /** 负向提示词。liblib API 要求 2000 字以内、纯英文。留空则用后端 nacos 的默认值 */
  negativePrompt?: string;
  /**
   * 兜底风格 ID。NSFW 命中 / 提示词超 1800 字 / 翻译失败时，改用这个 SuppX 风格作图。
   * 留空则不兜底、直接失败。只能填同源风格，否则用户会拿到完全无关的画风。
   */
  fallbackStyleId?: number;
  cfg?: number; // 提示词引导系数
  /** 采样器枚举值(liblib API 文档 7.5)。0=Euler a, 15=DPM++ 2M Karras 等。留空用后端默认 */
  sampler?: number;
  /** 采样步数 1~60。留空用后端默认 */
  steps?: number;
  /** 高清放大算法枚举值(liblib API 文档 7.6)。10=写实向, 11=动漫向。留空用后端默认 */
  upscaler?: number;
  /** 高清修复采样步数 1~30。留空用后端默认 */
  hiresSteps?: number;
  /** 高清修复去噪强度 0~1。留空用后端默认 */
  hiresDenoisingStrength?: number;
  versionUuid?: string;
  vaeId?: string;
  model?: string; // 模型
  isRecommend?: number;
  recommendSort?: number;
  isVipStyle?: number; // 是否会员风格
  minTierLevel?: number; // E15: 最低会员等级 -1=Free, 2=Lite, 0=Pro, 1=Premium
  createTime?: string;
  updateTime?: string;
  deleteStatus?: number;
  operator?: string;
}

/** 分页请求参数 */
export interface GetArtworkStylePageParams {
  pageNum?: number;
  pageSize?: number;
}

/** 分页响应结构 */
export interface ArtworkStylePageResponse {
  status: number;
  success: boolean;
  message: string;
  data: {
    records: ArtworkAiLiblibStyleDictDTO[];
    total: number;
    size: number;
    current: number;
    orders: Array<{ column: string; asc: boolean }>;
    pages: number;
  };
}

export interface CommonResponse {
  status: number;
  success: boolean;
  message: string;
  data: boolean;
}

/**
 * 获取 AI 风格字典分页数据
 * @param dto ArtworkAiLiblibStyleDictDTO 请求体
 * @param query 查询参数（分页）
 */
export const getArtworkStylePage = (
  dto: ArtworkAiLiblibStyleDictDTO,
  query?: GetArtworkStylePageParams
): Promise<AxiosResponse<ArtworkStylePageResponse>> => {
  return axiosInstance.post(`${baseUrl}/artworkAiLiblibStyle/page`, dto, {
    params: query,
  });
};

/**
 * 新增 AI 风格
 * @param dto 风格数据
 * @param file 图片文件
 */
export const addArtworkStyle = (
  dto: ArtworkAiLiblibStyleDictDTO
): Promise<AxiosResponse<CommonResponse>> => {
  return axiosInstance.post(`${baseUrl}/artworkAiLiblibStyle/add`, dto, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

/**
 * 修改 AI 风格
 * @param dto 风格数据
 * @param file 图片文件
 */
export const updateArtworkStyle = (
  dto: ArtworkAiLiblibStyleDictDTO
): Promise<AxiosResponse<CommonResponse>> => {
  return axiosInstance.post(`${baseUrl}/artworkAiLiblibStyle/update`, dto, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

/**
 * 删除 AI 风格
 * @param styleId 风格 ID
 */
export const deleteArtworkStyle = (
  styleId: number
): Promise<AxiosResponse<CommonResponse>> => {
  return axiosInstance.post(
    `${baseUrl}/artworkAiLiblibStyle/delete/${styleId}`
  );
};

/**
 * 获取 AI 风格详情
 * @param styleId 风格 ID
 */
export const getArtworkStyleDetail = (
  styleId: number
): Promise<
  AxiosResponse<{
    status: number;
    success: boolean;
    message: string;
    data: ArtworkAiLiblibStyleDictDTO;
  }>
> => {
  return axiosInstance.get(`${baseUrl}/artworkAiLiblibStyle/detail/${styleId}`);
};

/**
 * 获取全部 AI 风格类型（用于下拉菜单）
 */
export const getAllArtworkStyleTypes = (): Promise<
  AxiosResponse<{
    status: number;
    success: boolean;
    message: string;
    data: Partial<ArtworkAiLiblibStyleDictDTO>[];
  }>
> => {
  return axiosInstance.get(`${baseUrl}/artworkAiLiblibStyle/styleTypeAll`);
};

interface ArtworkAiDrawStyleTypeDTO {
  styleTypeId: number;
  styleType: string;
  styleTypeJa: string;
  styleTypeSort: number;
  defaultSelected: number;
  createTime: string;
  status: number;
  styleList: ArtworkAiDrawStyleDictDTO[];
}
interface ArtworkAiDrawStyleDictDTO {
  styleId: number;
  styleTypeId: number;
  styleName: string;
  styleNameJa: string;
  styleNameSort: number;
  styleImage: string;
  triggerWord: string;
  positivePrompt: string;
  positivePromptJa: string;
  versionUuid: string;
  vaeId: string;
  limitTime: number;
  model: number;
  description: string;
  descriptionJa: string;
  isSelfStyle: number;
  number: number;
  numberList: number[];
  defaultSelected: number;
  gcoins: number;
  pcoins: number;
}

interface ResponseDTOBoolean {
  status: number;
  success: boolean;
  message: string;
  data: boolean;
}

interface ResponseDTOPageArtworkAiDrawStyleTypeDTO {
  status: number;
  success: boolean;
  message: string;
  data: PageArtworkAiDrawStyleTypeDTO;
}

interface PageArtworkAiDrawStyleTypeDTO {
  records: ArtworkAiDrawStyleTypeDTO[];
  total: number;
  size: number;
  current: number;
  orders: OrderItem[];
  optimizeCountSql: PageArtworkAiDrawStyleTypeDTO;
  searchCount: PageArtworkAiDrawStyleTypeDTO;
  optimizeJoinOfCountSql: boolean;
  maxLimit: number;
  countId: string;
  pages: number;
}

interface OrderItem {
  column: string;
  asc: boolean;
}

interface ResponseDTO {
  status: number;
  success: boolean;
  message: string;
  data: object;
}

interface ResponseDTOArtworkAiDrawStyleTypeDTO {
  status: number;
  success: boolean;
  message: string;
  data: ArtworkAiDrawStyleTypeDTO;
}

/**
 * 修改风格数据
 * @param {*} params
 * @returns
 */
export const updateStyleDict = async (
  params: ArtworkAiDrawStyleTypeDTO
): Promise<AxiosResponse<ResponseDTOBoolean>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/artworkAiStyleType/update`,
    params
  );

  return response;
};

/**
 * 分页查询列表数据
 * @param {*} params
 * @returns
 */
export const queryPage = async (
  params: ArtworkAiDrawStyleTypeDTO & {
    pageNum: number;
    pageSize: number;
  }
): Promise<AxiosResponse<ResponseDTOPageArtworkAiDrawStyleTypeDTO>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/artworkAiStyleType/page?pageNum=${params.pageNum}&pageSize=${params.pageSize}`,
    params
  );

  return response;
};

/**
 * 删除风格数据
 * @param {*} params
 * @returns
 */
export const deleteStyleDict = async (params: {
  styleTypeId: number;
}): Promise<AxiosResponse<ResponseDTOBoolean>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/artworkAiStyleType/delete/${params.styleTypeId}`,
    params
  );

  return response;
};

/**
 * 新增风格数据
 * @param {*} params
 * @returns
 */
export const addStyleDict = async (
  params: ArtworkAiDrawStyleTypeDTO
): Promise<AxiosResponse<ResponseDTOBoolean>> => {
  const response = await axiosInstance.post(
    `${baseUrl}/artworkAiStyleType/add`,
    params
  );

  return response;
};

/**
 * 根据styleTypeId查风格
 * @param {*} params
 * @returns
 */
export const styleTypeById = async (params: {
  styleTypeId: number;
}): Promise<AxiosResponse<ResponseDTO>> => {
  const response = await axiosInstance.get(
    `${baseUrl}/artworkAiStyleType/styleTypeById`,
    { params }
  );

  return response;
};

/**
 * 查询全部风格类型
 * @param {*}
 * @returns
 */
export const artworkLiblibTypeStyle = async (): Promise<
  AxiosResponse<ResponseDTO>
> => {
  const response = await axiosInstance.get(
    `${baseUrl}/artworkAiStyleType/styleTypeAll`
  );

  return response;
};

/**
 * 详情
 * @param {*} params
 * @returns
 */
export const getGetDetail1 = async (params: {
  styleTypeId: number;
}): Promise<AxiosResponse<ResponseDTOArtworkAiDrawStyleTypeDTO>> => {
  const response = await axiosInstance.get(
    `${baseUrl}/artworkAiStyleType/detail/${params.styleTypeId}`,
    { params }
  );

  return response;
};
